import React, { useEffect, useState } from "react";
import { Layout } from "../components/Layout";
import { useDocumentMeta } from "../lib/useDocumentMeta";
import { useTheme } from "../lib/theme";

const SORO_EMBED = "https://app.trysoro.com/api/embed/1d56fd03-695f-4f1b-9f5d-f41f60e6bc3c";
const soroSrc = (theme) => (theme === "dark" ? `${SORO_EMBED}?theme=dark` : SORO_EMBED);

export const BlogPage = () => {
  const { theme } = useTheme();
  const [status, setStatus] = useState("loading"); // loading | ready | error

  useDocumentMeta({
    title: "Journal · Will Not Be Forgotten",
    description:
      "Long-form essays and field notes on civic memory, public history, and the dates this country has been asked to forget.",
    canonical: "https://willnotbeforgotten.org/blog",
  });

  // Load the embed and reload it when the site theme changes so the widget's
  // palette tracks light/dark. The widget renders into the #soro-blog div.
  useEffect(() => {
    setStatus("loading");
    const mount = document.getElementById("soro-blog");
    if (mount) mount.innerHTML = "";

    const script = document.createElement("script");
    script.src = soroSrc(theme);
    script.async = true;
    script.defer = true;
    script.setAttribute("data-soro-embed", "true");
    script.onload = () => setStatus("ready");
    script.onerror = () => setStatus("error");
    document.body.appendChild(script);

    return () => {
      script.remove();
      const node = document.getElementById("soro-blog");
      if (node) node.innerHTML = "";
    };
  }, [theme]);

  return (
    <Layout headerVariant="solid">
      <section data-testid="blog-hero" className="section section--no-border" style={{ paddingTop: "5rem", paddingBottom: "3rem" }}>
        <div className="shell">
          <span className="section__eyebrow">Journal</span>
          <h1 style={{ fontSize: "clamp(2.4rem, 4.5vw, 4rem)", lineHeight: 1, marginBottom: "1rem" }}>
            Field notes &amp; <em style={{ fontStyle: "italic", color: "hsl(var(--accent))" }}>essays</em>.
          </h1>
          <p className="section__lede" style={{ marginTop: 0, maxWidth: 760 }}>
            Long-form writing on civic memory, public history, and the work of preserving the dates this
            country has been asked to forget. New essays, interviews, and field notes appear here as they’re published.
          </p>
        </div>
      </section>

      <section data-testid="blog-articles" className="section">
        <div className="shell">
          <span className="section__eyebrow">Latest writing</span>

          {status === "loading" && (
            <p data-testid="blog-loading" style={{
              fontFamily: "var(--font-display)",
              fontStyle: "italic",
              fontSize: "1.1rem",
              color: "hsl(var(--muted))",
              marginTop: "1.5rem",
            }}>
              Loading the latest articles…
            </p>
          )}

          {status === "error" && (
            <p data-testid="blog-error" role="alert" style={{
              marginTop: "1.5rem",
              padding: "1rem 1.2rem",
              border: "1px solid hsl(var(--line-strong))",
              background: "hsl(var(--bg-elev))",
              color: "hsl(var(--ink-soft))",
              fontSize: "0.95rem",
            }}>
              The articles feed couldn’t load right now. Please refresh the page or check back shortly.
            </p>
          )}

          {/* Soro renders the published articles into this container. */}
          <div id="soro-blog" data-testid="soro-blog" style={{ marginTop: "2rem" }} />
        </div>
      </section>

      <section className="section" style={{ background: "hsl(var(--bg-deep))" }}>
        <div className="shell">
          <span className="section__eyebrow">Editorial principles</span>
          <h2 style={{ marginTop: 0 }}>How we read, and how we write.</h2>
          <div style={{
            marginTop: "2rem",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: "1.5rem",
          }}>
            {[
              {
                t: "Cite the source.",
                b: "Every claim should trace back to a primary document, museum, or named scholar. We treat citations as part of the prose, not an afterthought.",
              },
              {
                t: "Center the people.",
                b: "Histories happen to and through people. Pieces published here lean toward the witnesses, organizers, and survivors whose names belong on the masthead.",
              },
              {
                t: "Earn the urgency.",
                b: "Outrage is cheap. We earn the right to call something an emergency by showing the receipts — the votes, the laws, the silences, the omissions.",
              },
            ].map((p) => (
              <article key={p.t} style={{
                padding: "1.6rem",
                background: "hsl(var(--bg))",
                border: "1px solid hsl(var(--line))",
              }}>
                <h3 style={{
                  fontFamily: "var(--font-display)",
                  fontSize: "1.25rem",
                  fontWeight: 500,
                  marginTop: 0,
                  marginBottom: 8,
                }}>{p.t}</h3>
                <p style={{ fontSize: "0.95rem", lineHeight: 1.6, margin: 0 }}>{p.b}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};
